import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader, Loading, StatCard, inr } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { IndianRupee, TrendingUp, Building2, Wrench, Handshake, Users } from "lucide-react";

export default function AdminRevenue() {
  const [sum, setSum] = useState(null);
  const [txns, setTxns] = useState([]);
  const [renew, setRenew] = useState({ upcoming_renewals: [], expiring_trials: [] });
  const load = () => {
    api.get("/admin/revenue/summary").then((r) => setSum(r.data)).catch(() => setSum({}));
    api.get("/admin/revenue-transactions").then((r) => setTxns(r.data)).catch(() => {});
    api.get("/admin/revenue/renewals").then((r) => setRenew(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const markPaid = async (id) => { await api.post(`/admin/revenue-transactions/${id}/mark-paid`); toast.success("Marked paid"); load(); };

  if (!sum) return <Loading />;
  const s = sum.society || {}, v = sum.vendor || {}, m = sum.marketplace || {}, o = sum.overall || {};

  return (
    <div>
      <PageHeader title="Revenue Dashboard" subtitle="Recurring revenue across the four pillars." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={TrendingUp} label="Total MRR" value={inr(o.mrr_total)} tone="secondary" testid="rev-mrr" />
        <StatCard icon={IndianRupee} label="Collected" value={inr(o.collected)} tone="primary" testid="rev-collected" />
        <StatCard icon={IndianRupee} label="Outstanding" value={inr(o.outstanding)} tone="rose" testid="rev-outstanding" />
        <StatCard icon={TrendingUp} label="Society ARR" value={inr(s.arr)} tone="amber" testid="rev-arr" />
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-card border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3 font-semibold"><Building2 className="w-4 h-4 text-primary" /> Society ERP</div>
          <Row label="Active paid" value={s.active_paid} testid="rev-soc-active" />
          <Row label="Trial" value={s.trial} />
          <Row label="Expired" value={s.expired} />
          <Row label="Members billed" value={s.members_billed} />
          <Row label="MRR" value={inr(s.mrr)} />
          <Row label="Resident portal rev." value={inr(s.resident_portal_revenue)} />
        </div>
        <div className="bg-card border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3 font-semibold"><Wrench className="w-4 h-4 text-primary" /> Vendor Network</div>
          <Row label="Paid vendors" value={v.paid} testid="rev-vendor-paid" />
          <Row label="Waived" value={v.waived} />
          <Row label="MRR" value={inr(v.mrr)} />
          <Row label="ARR" value={inr(v.arr)} />
        </div>
        <div className="bg-card border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3 font-semibold"><Handshake className="w-4 h-4 text-primary" /> Marketplace</div>
          <Row label="Commission due" value={inr(m.commission_due)} />
          <Row label="Commission collected" value={inr(m.commission_collected)} />
          <p className="text-xs text-muted-foreground mt-3">Commission records populate as projects are awarded (Phase 3).</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-card border rounded-xl p-5" data-testid="renewals-upcoming">
          <div className="font-semibold mb-3">Upcoming Renewals (30 days)</div>
          {renew.upcoming_renewals.length === 0 ? <p className="text-sm text-muted-foreground py-4">No renewals due in the next 30 days.</p> :
            renew.upcoming_renewals.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
                <span><span className="text-xs uppercase text-muted-foreground mr-2">{r.type}</span>{r.name}</span>
                <span className="text-right"><span className="font-medium">{r.date}</span> <span className={`text-xs ${r.days_left <= 7 ? "text-rose-600" : "text-amber-600"}`}>({r.days_left}d)</span></span>
              </div>
            ))}
        </div>
        <div className="bg-card border rounded-xl p-5" data-testid="renewals-trials">
          <div className="font-semibold mb-3">Expiring Trials (30 days)</div>
          {renew.expiring_trials.length === 0 ? <p className="text-sm text-muted-foreground py-4">No trials expiring soon.</p> :
            renew.expiring_trials.slice(0, 8).map((r, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
                <span><span className="text-xs uppercase text-muted-foreground mr-2">{r.type}</span>{r.name}</span>
                <span className="text-right"><span className="font-medium">{r.date}</span> <span className={`text-xs ${r.days_left <= 7 ? "text-rose-600" : "text-amber-600"}`}>({r.days_left}d)</span></span>
              </div>
            ))}
          {renew.expiring_trials.length > 8 && <p className="text-xs text-muted-foreground pt-2">+{renew.expiring_trials.length - 8} more</p>}
        </div>
      </div>

      <div className="bg-card border rounded-xl overflow-x-auto">
        <div className="px-5 pt-4 font-semibold">Revenue Transactions</div>
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Source</TableHead><TableHead>Description</TableHead><TableHead>Amount</TableHead><TableHead>GST</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {txns.map((t) => (
              <TableRow key={t.txn_id} data-testid={`rev-txn-${t.txn_id}`}>
                <TableCell className="text-sm">{(t.created_at || "").slice(0, 10)}</TableCell>
                <TableCell className="text-xs capitalize">{(t.source || "").replace("_", " ")}</TableCell>
                <TableCell className="text-sm">{t.description}</TableCell>
                <TableCell>{inr(t.amount)}</TableCell><TableCell>{inr(t.gst)}</TableCell>
                <TableCell className="font-semibold">{inr(t.total)}</TableCell>
                <TableCell><span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${t.status === "paid" ? "bg-secondary/15 text-secondary" : t.status === "waived" ? "bg-primary/10 text-primary" : "bg-amber-100 text-amber-700"}`}>{t.status}</span></TableCell>
                <TableCell>{t.status === "due" && <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => markPaid(t.txn_id)} data-testid={`rev-markpaid-${t.txn_id}`}>Mark Paid</Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {txns.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No revenue transactions yet.</p>}
      </div>
    </div>
  );
}

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between py-1.5 text-sm border-b last:border-0"><span className="text-muted-foreground">{label}</span><span className="font-medium" data-testid={testid}>{value ?? 0}</span></div>
);
